<!--
 * Path: src/features/browser/forms/mindmap/MindmapRead.vue
 * Task: Renders a mindmap layout showing predecessors and successors.
 * Update: Fixed accumulated v-for array refs causing broken line calculations, and enforced solid backgrounds to prevent lines from shining through.
-->
<template>
  <div :class="{ 'mb-6': !hideHeader }">
    <SubHeader
      v-if="!hideHeader"
      title="Connected Artifacts"
      info="Visualization of predecessor and successor relationships."
      @click:info="$emit('show-info')"
    />

    <div class="mindmap-scroll-container">
      <div class="mindmap-layout-wrapper" ref="containerRef">
        <!-- SVG Connection Lines -->
        <svg class="connections-svg">
          <path
            v-for="(line, index) in connectionLines"
            :key="index"
            :d="line"
            fill="none"
            stroke-width="2.5"
            class="connection-path"
            :class="{ 'path-anim': isTogglingPane }"
          />
        </svg>

        <!-- Grid Layout applying the animation when the detail pane toggles -->
        <div class="mindmap-grid" :class="{ 'is-vertical': isVertical, 'pane-toggle-anim': isTogglingPane }">

          <!-- Column 1: Predecessors (Left or Top) -->
          <div class="mindmap-col col-left">
            <div
              v-for="item in predecessors"
              :key="item.id"
              class="mindmap-node pred-node"
              :ref="(el) => { if (el) predNodeRefs.push(el as HTMLElement) }"
            >
              <!-- Enforcing bg-surface to ensure the line doesn't shine through the transparent card -->
              <EntityPanel :entity="item" :is-link="true" :in-right-pane="inRightPane" style="background-color: rgb(var(--v-theme-surface)) !important;" />
            </div>
            <div v-if="predecessors.length === 0" class="text-caption text-disabled font-italic">
              No Predecessors
            </div>
          </div>

          <!-- Column 2: Active Center Node -->
          <div class="mindmap-col col-center">
            <div class="mindmap-node center-node w-100" ref="centerNodeRef">
              <EntityPanel v-if="currentEntity" :entity="currentEntity" class="active-node" style="background-color: rgb(var(--v-theme-surface)) !important;" />
            </div>
          </div>

          <!-- Column 3: Successors (Right or Bottom) -->
          <div class="mindmap-col col-right">
            <div
              v-for="item in successors"
              :key="item.id"
              class="mindmap-node succ-node"
              :ref="(el) => { if (el) succNodeRefs.push(el as HTMLElement) }"
            >
              <EntityPanel :entity="item" :is-link="true" :in-right-pane="inRightPane" style="background-color: rgb(var(--v-theme-surface)) !important;" />
            </div>
            <div v-if="successors.length === 0" class="text-caption text-disabled font-italic">
              No Successors
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, inject, watch, onBeforeUpdate, nextTick } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useDatastoreStore } from '@/store/datastore';
import { useUiStore } from '@/store/ui';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';

import SubHeader from '@/core/components/forms/SubHeader.vue';
import EntityPanel from '@/core/components/common/EntityPanel.vue';

interface Props {
  entity?: Entity | null;
  inRightPane?: boolean;
  hideHeader?: boolean;
}

const { entity = null, inRightPane = false, hideHeader = false } = defineProps<Props>();
const emit = defineEmits<{ (e: 'show-info'): void }>();

const workspaceStore = useWorkspaceStore();
const datastoreStore = useDatastoreStore();
const uiStore = useUiStore();
const formContext = inject(FormContextKey, null);

// Template Refs for robust DOM access
const containerRef = ref<HTMLElement | null>(null);
const centerNodeRef = ref<HTMLElement | null>(null);
const predNodeRefs = ref<HTMLElement[]>([]);
const succNodeRefs = ref<HTMLElement[]>([]);

const connectionLines = ref<string[]>([]);
const isVertical = ref<boolean>(false);
const isTogglingPane = ref<boolean>(false);

const currentEntity = computed<Entity | null>(() => {
  if (entity) return entity;
  return formContext ? formContext.entity.value : workspaceStore.selectedEntity;
});

// Resolves a list of string IDs to actual strongly-typed Entity objects
const resolveEntities = (ids: string[] | undefined): Entity[] => {
  if (!ids || !Array.isArray(ids)) return [];
  const all = datastoreStore.allEntities;
  return ids.map(id => all.find((e: Entity) => e.id === id)).filter((e): e is Entity => !!e);
};

const predecessors = computed<Entity[]>(() => resolveEntities(currentEntity.value?.predecessorIds));
const successors = computed<Entity[]>(() => resolveEntities(currentEntity.value?.successorIds));

// CRITICAL VUE 3 FIX: Reset ref arrays before DOM updates to prevent accumulating dead DOM elements
onBeforeUpdate(() => {
  predNodeRefs.value = [];
  succNodeRefs.value = [];
});

const updateLines = (): void => {
  if (!containerRef.value) return;
  const containerRect = containerRef.value.getBoundingClientRect();

  // Safe extraction to support both arrays (if wrapped in v-for) and single HTMLElements
  const centerEl = Array.isArray(centerNodeRef.value) ? centerNodeRef.value[0] : centerNodeRef.value;

  if (!centerEl) {
    connectionLines.value = [];
    return;
  }

  const centerRect = (centerEl as HTMLElement).getBoundingClientRect();

  // Calculate relative coordinates for the center node
  const cPos = {
    left: centerRect.left - containerRect.left,
    right: centerRect.right - containerRect.left,
    centerY: (centerRect.top - containerRect.top) + (centerRect.height / 2)
  };

  const newLines: string[] = [];

  // Draw lines from predecessors to center
  predNodeRefs.value.forEach((node) => {
    if (!node) return;
    const el = (node as any).$el || node;
    const rect = (el as HTMLElement).getBoundingClientRect();

    if (isVertical.value) {
      // Vertical orientation: Left edge of pred to Left edge of center
      const startX = rect.left - containerRect.left;
      const startY = (rect.top - containerRect.top) + (rect.height / 2);
      const endX = cPos.left;
      const endY = cPos.centerY;
      // Bend the curve cleanly out to the left side
      const dist = 30 + Math.abs(endY - startY) * 0.25;
      newLines.push(`M ${startX} ${startY} C ${startX - dist} ${startY}, ${endX - dist} ${endY}, ${endX} ${endY}`);
    } else {
      // Horizontal orientation: Right edge of pred to Left edge of center
      const startX = rect.right - containerRect.left;
      const startY = (rect.top - containerRect.top) + (rect.height / 2);
      const endX = cPos.left;
      const endY = cPos.centerY;
      const dist = Math.abs(endX - startX) * 0.5;
      newLines.push(`M ${startX} ${startY} C ${startX + dist} ${startY}, ${endX - dist} ${endY}, ${endX} ${endY}`);
    }
  });

  // Draw lines from center to successors
  succNodeRefs.value.forEach((node) => {
    if (!node) return;
    const el = (node as any).$el || node;
    const rect = (el as HTMLElement).getBoundingClientRect();

    if (isVertical.value) {
      // Vertical orientation: Right edge of center to Right edge of succ
      const startX = cPos.right;
      const startY = cPos.centerY;
      const endX = rect.right - containerRect.left;
      const endY = (rect.top - containerRect.top) + (rect.height / 2);
      // Bend the curve cleanly out to the right side
      const dist = 30 + Math.abs(endY - startY) * 0.25;
      newLines.push(`M ${startX} ${startY} C ${startX + dist} ${startY}, ${endX + dist} ${endY}, ${endX} ${endY}`);
    } else {
      // Horizontal orientation: Right edge of center to Left edge of succ
      const startX = cPos.right;
      const startY = cPos.centerY;
      const endX = rect.left - containerRect.left;
      const endY = (rect.top - containerRect.top) + (rect.height / 2);
      const dist = Math.abs(endX - startX) * 0.5;
      newLines.push(`M ${startX} ${startY} C ${startX + dist} ${startY}, ${endX - dist} ${endY}, ${endX} ${endY}`);
    }
  });

  connectionLines.value = newLines;
};

// RequestAnimationFrame for seamless 60fps redrawing during container resizes
let rafId: number | null = null;
const triggerUpdate = (): void => {
  if (rafId) window.cancelAnimationFrame(rafId);
  rafId = window.requestAnimationFrame(() => {
    updateLines();
  });
};

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  triggerUpdate();

  if (containerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Toggle vertical mode if the container is narrower than 750px
        const newIsVertical = entry.contentRect.width < 750;
        if (isVertical.value !== newIsVertical) {
          isVertical.value = newIsVertical;
        }
      }
      triggerUpdate();
    });
    resizeObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect();
  if (rafId) window.cancelAnimationFrame(rafId);
});

// Watch for detail pane toggles to trigger visual animation
watch(() => uiStore.panels.showSecondaryDetails, () => {
  isTogglingPane.value = true;
  setTimeout(() => {
    isTogglingPane.value = false;
  }, 400); // Matches the CSS animation duration
});

// Trigger line recalculations when connections change
watch([predecessors, successors, currentEntity], async () => {
  // Ensure the DOM has fully rendered the V-For loops before calculating bounding rects
  await nextTick();
  triggerUpdate();
}, { deep: true, immediate: true });

</script>

<style scoped>
.mindmap-scroll-container {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 16px;
}

.mindmap-layout-wrapper {
  position: relative;
  width: 100%;
  padding: 24px 0;
}

/* Base SVG setup */
.connections-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
}

.connection-path {
  stroke: rgba(var(--v-theme-on-surface), 0.25);
  /* Removed static 'd' transition for direct 60fps matching to nodes */
}

/* Draw animation on the SVG paths triggered by detail pane toggle */
.path-anim {
  stroke-dasharray: 2000;
  stroke-dashoffset: 2000;
  animation: drawLine 0.4s ease-out forwards;
}

@keyframes drawLine {
  to { stroke-dashoffset: 0; }
}

/* Fade and scale animation on the grid elements triggered by detail pane toggle */
.pane-toggle-anim {
  animation: fadeSlide 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

@keyframes fadeSlide {
  0% { opacity: 0; transform: scale(0.97); }
  100% { opacity: 1; transform: scale(1); }
}

.mindmap-grid {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 2;
  gap: 32px;
}

/* Vertical layout specific rules */
.mindmap-grid.is-vertical {
  flex-direction: column;
  gap: 48px;
}

.mindmap-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
}

.mindmap-grid.is-vertical .mindmap-col {
  align-items: center !important;
  width: 100%;
}

.col-left {
  flex: 1;
  align-items: flex-end;
}

.col-center {
  flex: 0 0 auto;
  width: 280px;
  align-items: center;
}

.col-right {
  flex: 1;
  align-items: flex-start;
}

.mindmap-node {
  width: 100%;
  max-width: 260px;
  z-index: 2;
}

:deep(.active-node) {
  cursor: default !important;
  border: 2px solid rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.15) !important;
}
</style>
